import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AudioLines, Gift, ScrollText, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { AI_TERMS_VERSION } from '../lib/nexusAiConstants';

export { AI_TERMS_VERSION } from '../lib/nexusAiConstants';

/**
 * First-run gate for Nexus AI (Help + Voice).
 * Flow: intro → (optional) claim 1M free tokens → AI Terms of Service.
 * Terms are per user; the free-token gift is a one-time claim per business.
 */

type Step = 'intro' | 'claim' | 'terms';

interface NexusVoiceIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the flow is complete — start Help or Voice here. */
  onAccepted: () => void;
  businessId?: string | null;
  /** Skip the claim screen when this business already claimed the gift. */
  welcomeAlreadyClaimed?: boolean;
  /** Skip terms when this user already accepted the current version. */
  termsAlreadyAccepted?: boolean;
}

export default function NexusVoiceIntroModal({
  isOpen,
  onClose,
  onAccepted,
  businessId,
  welcomeAlreadyClaimed = false,
  termsAlreadyAccepted = false,
}: NexusVoiceIntroModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('intro');
  const [isSaving, setIsSaving] = useState(false);
  const [claimedThisSession, setClaimedThisSession] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('intro');
      setClaimedThisSession(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const needsClaim = Boolean(businessId) && !welcomeAlreadyClaimed && !claimedThisSession;
  const needsTerms = !termsAlreadyAccepted;

  const goAfterIntro = () => {
    if (needsClaim) setStep('claim');
    else if (needsTerms) setStep('terms');
    else {
      onClose();
      onAccepted();
    }
  };

  const goAfterClaim = () => {
    if (needsTerms) setStep('terms');
    else {
      onClose();
      onAccepted();
    }
  };

  const handleClaimTokens = async () => {
    if (!supabase || !user?.id || !businessId || isSaving) return;
    setIsSaving(true);
    try {
      const { data, error } = await supabase.rpc('claim_ai_welcome_tokens', {
        p_business_id: businessId,
      });
      if (error) throw error;
      const result = data as { ok?: boolean; already_claimed?: boolean; tokens_granted?: number } | null;
      if (!result?.ok) throw new Error('Claim failed');

      setClaimedThisSession(true);
      if (result.already_claimed) {
        toast.success('Welcome tokens were already claimed for this business.');
      } else {
        toast.success('1,000,000 AI tokens added to your account!');
      }
      goAfterClaim();
    } catch (err) {
      console.error('Error claiming welcome AI tokens:', err);
      toast.error('Could not claim free tokens. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!supabase || !user?.id || isSaving) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('user_ai_terms_acceptance')
        .upsert(
          {
            user_id: user.id,
            version: AI_TERMS_VERSION,
            accepted_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );
      if (error) throw error;

      onClose();
      onAccepted();
    } catch (err) {
      console.error('Error saving AI terms acceptance:', err);
      toast.error('Could not save your acceptance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const headerIcon =
    step === 'intro' ? <AudioLines className="h-7 w-7" />
      : step === 'claim' ? <Gift className="h-7 w-7" />
        : <ScrollText className="h-7 w-7" />;

  const headerTitle =
    step === 'intro' ? 'Meet Nexus AI'
      : step === 'claim' ? 'Free AI Tokens'
        : 'AI Terms of Service';

  const headerSubtitle =
    step === 'intro' ? 'Your AI assistant for Help and Voice mode'
      : step === 'claim' ? 'A one-time gift to get your business started'
        : 'One quick thing before you start';

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-blue-500 to-teal-600 text-white text-center flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-white/15 border border-white/25 flex items-center justify-center">
            {headerIcon}
          </div>
          <h2 className="text-xl font-bold">{headerTitle}</h2>
          <p className="mt-1 text-sm text-white/85">{headerSubtitle}</p>
        </div>

        {step === 'intro' && (
          <>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Ask anything</p>
                  <p className="text-sm text-gray-500">Get instant answers and step-by-step guidance for any app in Nexus — typed Help or live Voice.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Hands-free actions</p>
                  <p className="text-sm text-gray-500">Create bookings, add customers, check messages, navigate the app and more — all by voice.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Context aware</p>
                  <p className="text-sm text-gray-500">Nexus can see the screen you're on, so help always fits what you're doing right now.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Powered by AI tokens</p>
                  <p className="text-sm text-gray-500">Help and Voice use your business AI token balance — same system as your other AI features.</p>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2 flex-shrink-0">
              <button
                onClick={goAfterIntro}
                className="w-full py-3 rounded-2xl bg-gradient-to-br from-blue-500 to-teal-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 'claim' && (
          <>
            <div className="px-6 py-5 overflow-y-auto space-y-4">
              <div className="text-center py-2">
                <p className="text-4xl font-bold text-gray-900 tracking-tight">1,000,000</p>
                <p className="text-sm font-semibold text-teal-700 mt-1">free AI tokens</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-[13px] leading-relaxed text-gray-600 space-y-2">
                <p>
                  Claim a one-time gift of <span className="font-semibold text-gray-800">1 million AI tokens</span> for this business — enough to explore Help, Voice, and other AI features while you set up.
                </p>
                <p>
                  This offer can only be claimed <span className="font-semibold text-gray-800">once per business</span>. Tokens are added to your shared AI token balance and work the same as purchased tokens.
                </p>
                <p className="text-gray-500">
                  When you run low later, you can buy more anytime from AI Settings.
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2 space-y-2 flex-shrink-0">
              <button
                onClick={handleClaimTokens}
                disabled={isSaving || !businessId}
                className="w-full py-3 rounded-2xl bg-gradient-to-br from-blue-500 to-teal-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-60"
              >
                {isSaving ? 'Claiming…' : 'Claim 1,000,000 tokens'}
              </button>
              <button
                onClick={onClose}
                disabled={isSaving}
                className="w-full py-2.5 rounded-2xl text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Not now
              </button>
            </div>
          </>
        )}

        {step === 'terms' && (
          <>
            <div className="px-6 py-5 overflow-y-auto">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-[13px] leading-relaxed text-gray-600 space-y-3">
                <p className="font-semibold text-gray-900">Nexus AI Services Terms</p>
                <p>
                  These AI Services Terms ("AI Terms") govern your use of the artificial intelligence features ("AI Services") provided by Nexus. By accessing or using any AI Services, you acknowledge that you have read, understood, and agree to these AI Terms in addition to the general Terms of Service.
                </p>

                <p className="font-semibold text-gray-800 pt-1">1. Beta Technology</p>
                <p>The AI Services are provided as a beta feature and remain under active development.</p>
                <p>The AI Services may contain bugs, errors, inaccuracies, unexpected behaviour, interruptions, or incomplete functionality. Features, capabilities, and performance may change at any time without notice.</p>
                <p>You acknowledge that the AI Services are experimental and should not be relied upon as the sole basis for making business decisions.</p>

                <p className="font-semibold text-gray-800 pt-1">2. No Guarantee of Accuracy</p>
                <p>Artificial intelligence can generate inaccurate, incomplete, misleading, outdated, or incorrect information.</p>
                <p>Nexus makes no representations, warranties, or guarantees regarding:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>the accuracy of AI-generated content;</li>
                  <li>the completeness of AI-generated content;</li>
                  <li>the reliability of AI-generated content;</li>
                  <li>the legality or compliance of AI-generated content;</li>
                  <li>whether AI-generated actions reflect your intentions; or</li>
                  <li>whether AI-generated communications are appropriate for your customers.</li>
                </ul>
                <p>You are solely responsible for reviewing and verifying all AI-generated content and actions before relying upon them where appropriate.</p>

                <p className="font-semibold text-gray-800 pt-1">3. AI Actions Authorized by You</p>
                <p>The AI Services may, when enabled by you, perform actions within your account including but not limited to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>creating bookings;</li>
                  <li>scheduling appointments;</li>
                  <li>generating estimates;</li>
                  <li>generating proposals;</li>
                  <li>creating invoices;</li>
                  <li>sending invoices;</li>
                  <li>sending SMS messages;</li>
                  <li>sending emails;</li>
                  <li>replying to customer communications;</li>
                  <li>updating customer records;</li>
                  <li>summarizing conversations;</li>
                  <li>retrieving account information;</li>
                  <li>organizing business data; and</li>
                  <li>performing other actions made available within the platform.</li>
                </ul>
                <p>By enabling or using these features, you authorize Nexus and its AI Services to perform these actions on your behalf within the permissions you have granted.</p>
                <p>You acknowledge that AI-generated actions may not always reflect your intended outcome.</p>

                <p className="font-semibold text-gray-800 pt-1">4. Your Responsibility</p>
                <p>You remain solely responsible for all actions taken through your account, including actions initiated, suggested, drafted, or performed by the AI Services.</p>
                <p>You are responsible for:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>reviewing important communications;</li>
                  <li>confirming pricing;</li>
                  <li>verifying customer information;</li>
                  <li>ensuring estimates and invoices are accurate;</li>
                  <li>complying with applicable laws;</li>
                  <li>ensuring messages sent to customers are appropriate; and</li>
                  <li>correcting any AI mistakes.</li>
                </ul>
                <p>Use of the AI Services is entirely at your own risk.</p>

                <p className="font-semibold text-gray-800 pt-1">5. Limitation of Liability</p>
                <p>To the fullest extent permitted by applicable law, Nexus shall not be liable for any direct, indirect, incidental, consequential, special, exemplary, punitive, or economic damages arising out of or relating to AI-generated content, communications, recommendations, summaries, calculations, decisions, business actions, customer disputes, loss of business, lost profits, missed appointments, incorrect invoices, or any other loss arising from the use or inability to use the AI Services.</p>
                <p>You acknowledge that all AI output should be independently evaluated before being relied upon.</p>

                <p className="font-semibold text-gray-800 pt-1">6. No Professional Advice</p>
                <p>AI-generated responses do not constitute legal, financial, accounting, tax, insurance, employment, compliance, engineering, medical, or other professional advice.</p>

                <p className="font-semibold text-gray-800 pt-1">7. Third-Party AI Providers</p>
                <p>The AI Services rely on third-party artificial intelligence providers. Information submitted through AI features may be transmitted to these providers for processing. Nexus does not control their operation, availability, security practices, or model behaviour.</p>

                <p className="font-semibold text-gray-800 pt-1">8. Data Processing</p>
                <p>To provide AI functionality, Nexus may transmit prompts, customer information, business records, communications, documents, scheduling information, and other data necessary to generate AI responses. You represent that you have all necessary rights to submit such information for AI processing.</p>

                <p className="font-semibold text-gray-800 pt-1">9. Availability</p>
                <p>AI Services may be modified, suspended, rate limited, restricted, or discontinued at any time without notice. Nexus does not guarantee uninterrupted availability of any AI feature.</p>

                <p className="font-semibold text-gray-800 pt-1">10. Acceptable Use</p>
                <p>You agree not to use the AI Services to violate any law, infringe rights, generate unlawful or deceptive content, send spam, harass others, circumvent security, reverse engineer the AI Services, attempt prompt injection, or otherwise misuse the AI Services.</p>

                <p className="font-semibold text-gray-800 pt-1">11. Abuse and Restrictions</p>
                <p>Nexus may monitor AI usage for security, fraud prevention, abuse detection, and service improvement, and may suspend or terminate AI access for abuse or violations of these AI Terms.</p>

                <p className="font-semibold text-gray-800 pt-1">12. Feedback</p>
                <p>You grant Nexus a perpetual, worldwide, royalty-free license to use feedback, suggestions, feature requests, or ideas relating to the AI Services without restriction or compensation.</p>

                <p className="font-semibold text-gray-800 pt-1">13. Disclaimer of Warranties</p>
                <p>THE AI SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXUS DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE.</p>

                <p className="font-semibold text-gray-800 pt-1">14. Indemnification</p>
                <p>You agree to defend, indemnify, and hold harmless Nexus and its affiliates from any claims arising out of your use of the AI Services, reliance upon AI-generated output, actions performed using AI, content submitted to AI, or your violation of these AI Terms or any law.</p>

                <p className="font-semibold text-gray-800 pt-1">15. Changes</p>
                <p>Nexus may modify these AI Terms or the AI Services at any time. Continued use of the AI Services after changes become effective constitutes acceptance of the updated AI Terms.</p>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2 space-y-2 flex-shrink-0">
              <button
                onClick={handleAcceptTerms}
                disabled={isSaving}
                className="w-full py-3 rounded-2xl bg-gradient-to-br from-blue-500 to-teal-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-60"
              >
                {isSaving ? 'Saving…' : 'Agree & Continue'}
              </button>
              <button
                onClick={onClose}
                disabled={isSaving}
                className="w-full py-2.5 rounded-2xl text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
